import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Icon } from '../../ui';
import { useAppConfig } from '../configContext';
import { endSession, LOGOUT_DESTINATION } from './logout';
import styles from './AppShell.module.css';

const CHANNEL = 'adorchestrator-session';
// Multiple responsive controls can subscribe in the same document.
let leaving = false;
let restoring = false;

export function announceLogout() {
  leaving = true;
  if ('BroadcastChannel' in window) {
    const channel = new BroadcastChannel(CHANNEL);
    channel.postMessage('logout');
    channel.close();
  }
}

export function LogoutButton({ member = false }: { member?: boolean }) {
  const { apiBuild } = useAppConfig();
  return member || apiBuild ? <SessionLogout member={member} /> : null;
}

export function useLogoutEvents(member: boolean) {
  const queryClient = useQueryClient();
  const { apiBuild } = useAppConfig();
  // Remove loaded account data in other open tabs as soon as this browser signs out.
  useEffect(() => {
    if (!member && !apiBuild) return;
    leaving = false;
    restoring = false;
    const channel = 'BroadcastChannel' in window ? new BroadcastChannel(CHANNEL) : null;
    if (channel)
      channel.onmessage = (event: MessageEvent<unknown>) => {
        if (event.data === 'logout' && !leaving) {
          leaving = true;
          queryClient.clear();
          window.location.replace(LOGOUT_DESTINATION);
        }
      };
    const restore = (event: PageTransitionEvent) => {
      if (event.persisted && !restoring) {
        restoring = true;
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', restore);
    return () => {
      channel?.close();
      window.removeEventListener('pageshow', restore);
    };
  }, [member, apiBuild, queryClient]);
}

function SessionLogout({ member }: { member: boolean }) {
  const queryClient = useQueryClient();
  const { passwordGate } = useAppConfig();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  useLogoutEvents(member);

  async function logout() {
    setPending(true);
    setError(false);
    try {
      const destination = await endSession(member, passwordGate);
      await queryClient.cancelQueries();
      queryClient.clear();
      announceLogout();
      window.location.replace(destination);
    } catch {
      setError(true);
      setPending(false);
    }
  }
  return (
    <div className={styles.logoutGroup}>
      <button
        type="button"
        className={styles.logoutButton}
        title="Log out"
        aria-label={pending ? 'Logging out' : 'Log out'}
        disabled={pending}
        onClick={() => void logout()}
      >
        <Icon name="lock" size={16} />
        <span className={styles.navLabel}>{pending ? 'Logging out…' : 'Log out'}</span>
      </button>
      {error && <p role="alert">Couldn’t log out. Please try again.</p>}
    </div>
  );
}
